import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { isEmpty, trim } from "lodash/fp";

import type { SxProps } from "@mui/material";

import { SearchCompatibleAnnotatedText } from "@app/analytics/components/AnnotatedText";
import type { TextChunk } from "@app/common/types";

import { Row } from "@rui/Stack";
import { Span } from "@rui/styles";

import {
  DeleteButton,
  DiscardButton,
  EditableContentWrapper,
  EditableSpan,
  EditableSpanWrapper,
  EditActionsContainer,
  Placeholder,
  SaveButton,
} from "./styles";

export interface EditableContentProps {
  editing: boolean;
  contentChunks?: TextChunk[] | string;
  onDelete?: () => void;
  onDiscard?: () => void;
  onEnter?: (
    event: React.KeyboardEvent,
    opts: { preValue: string; postValue: string }
  ) => void;
  onSave: (updatedValue: string) => void;
  onToggleEditing: (editing: boolean) => void;
  onInput?: (updatedValue: string) => void;
  placeholder?: string;
  prefix?: React.ReactNode;
  value: string;
  type: string;
  sx?: SxProps;
}

const getCursorRange = () => document?.getSelection()?.getRangeAt(0);
const getCursorPosition = () => getCursorRange()?.startOffset;

const getSplitValue = (value = "") => {
  const cursorPosition = getCursorPosition() ?? value.length;
  return [
    trim(value.slice(0, cursorPosition)),
    trim(value.slice(cursorPosition)),
  ];
};

const getContentItems = ({
  currentValue,
  chunks,
  editing,
}: {
  currentValue: string;
  chunks: EditableContentProps["contentChunks"];
  editing: boolean;
}) => {
  if (editing || typeof chunks === "string" || !chunks || isEmpty(chunks)) {
    return currentValue;
  }

  return chunks.map((chunk) => {
    if (chunk.highlighted) {
      return (
        <SearchCompatibleAnnotatedText
          key={chunk.uniqueKey}
          highlighted
          searchIndex={chunk.highlightIndex}
        >
          {chunk.content}
        </SearchCompatibleAnnotatedText>
      );
    }
    return chunk.content;
  });
};

const EditableContent = ({
  editing,
  onDelete,
  onDiscard,
  onEnter,
  onSave,
  onToggleEditing,
  onInput,
  placeholder,
  prefix = null,
  value,
  type,
  contentChunks,
  sx,
}: EditableContentProps) => {
  const [key, setKey] = useState(0);
  const [cursorOffset, setCursorOffset] = useState<number | undefined>();
  const inputRef = useRef<HTMLSpanElement | null>(null);
  const valueRef = useRef<string>(value);

  const syncValueRef = useCallback(() => {
    if (valueRef.current === value) return;
    valueRef.current = value;
    setKey((x) => x + 1);
  }, [value]);

  useEffect(() => {
    if (!editing) return;
    // short timeout to allow time for the input to appear
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [editing]);

  useEffect(() => {
    // if the value coming into the component changes, update the ref
    syncValueRef();
  }, [value]);

  // this effect will ensure that the cursor does not jump to the front
  // of the contentEditable span when text is wrapped to the next line
  useLayoutEffect(() => {
    if (
      cursorOffset !== undefined &&
      inputRef.current &&
      !isEmpty(inputRef.current.childNodes)
    ) {
      const textLength = String(
        inputRef.current.childNodes[0].textContent
      ).length;
      // don't try to set a range start longer than the text length
      if (cursorOffset <= textLength) {
        const newRange = document.createRange();
        newRange.setStart(inputRef.current.childNodes[0], cursorOffset);
        const selection = document.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(newRange);
      }
    }
  }, [cursorOffset, inputRef]);

  const handleInput = (event: React.ChangeEvent<HTMLSpanElement>) => {
    const updatedText = event.currentTarget.textContent as string;

    valueRef.current = updatedText;

    setCursorOffset(getCursorPosition());

    onInput?.(updatedText);
  };

  const handleSave = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    inputRef.current?.blur();

    onSave(trim(valueRef.current));
    setCursorOffset(undefined);
    onToggleEditing(false);
  };

  const handleDiscard = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    inputRef.current?.blur();

    syncValueRef();
    onDiscard?.();
    setCursorOffset(undefined);
    onToggleEditing(false);
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!onDelete) return;

    onDelete();
    setCursorOffset(undefined);
    onToggleEditing(false);
  };

  const handleEnter = (event: React.KeyboardEvent) => {
    event.preventDefault();

    const hasModifierKey = event.shiftKey || event.metaKey || event.ctrlKey;
    if (!hasModifierKey) {
      handleSave(event);
      return;
    }

    // do nothing on enter if the value is currently empty
    if (!onEnter || !valueRef.current) return;
    const [preValue, postValue] = getSplitValue(valueRef.current);
    onEnter(event, { preValue, postValue });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "Enter":
        handleEnter(event);
        break;
      case "Escape":
        handleDiscard(event);
        break;
      default:
        break;
    }
  };

  // Used to debounce double click selections
  const enterEditModeTimeout = useRef(0);

  const handleMouseUp = () => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();

    window.clearTimeout(enterEditModeTimeout.current);
    if ((!selection || selectedText === "") && !editing) {
      enterEditModeTimeout.current = window.setTimeout(() => {
        onToggleEditing(true);
        inputRef.current?.focus();
      }, 200);
    }
  };

  const showPlaceholder =
    Boolean(placeholder) && !editing && !value && !valueRef.current;

  const contentItems = getContentItems({
    currentValue: valueRef.current,
    editing,
    chunks: contentChunks,
  });

  return (
    <EditableContentWrapper
      onMouseUp={handleMouseUp}
      className={editing ? "focused" : undefined}
    >
      <EditableSpanWrapper className="editable-content__input-wrapper">
        <span>{prefix}</span>
        <Span position="relative" width="100%">
          {showPlaceholder && (
            <Placeholder className={showPlaceholder ? undefined : "hidden"}>
              {placeholder}
            </Placeholder>
          )}
          <EditableSpan
            key={key}
            className="editable-content__input"
            ref={inputRef}
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            sx={sx}
          >
            {contentItems}
          </EditableSpan>
        </Span>
      </EditableSpanWrapper>
      {editing && (
        <EditActionsContainer variant="apart">
          <Row variant="left" spacing={1}>
            <DeleteButton
              onClick={handleDelete}
              type={type}
              disabled={!onDelete}
            />
          </Row>
          <Row variant="right" spacing={1}>
            <DiscardButton onClick={handleDiscard} />
            <SaveButton onClick={handleSave} />
          </Row>
        </EditActionsContainer>
      )}
    </EditableContentWrapper>
  );
};

export default EditableContent;
